import React, { useState, useEffect } from 'react';

import moment from 'moment';
import _ from 'lodash';
import cx from 'classnames';

import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Pie,
  PieChart,
  Sector,
  Cell,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip
} from 'recharts';

import { EmptyMessage } from '.';
import Truncate from "react-truncate";
import { formatDurationWithLabel } from '../reporthelpers';

import styles from '../report.module.scss';

export function TrendsLineChart({ data, height }) {

  if (!data || data.length < 1) {
    return null;
  }

  var chartData = data.map(item => {
    return {
      key: moment(item.key, 'DD-MM-YYYY').format('DD MMM \nYYYY'),
      value: item.value
    }
  })

  const CustomTick = ({ x, y, payload }) => {
    const [line1, line2] = payload.value.split('\n');
    const offset = 8;
  
    return (
      <foreignObject x={x - 25} y={y + offset} width="50" height="50">
        <div style={{ textAlign: 'center', fontSize: '12px', fontFamily: 'Arial' }}>
          <div>{line1}</div>
          <div>{line2}</div>
        </div>
      </foreignObject>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart
        data={chartData}
        margin={{
          top: 5,
          right: 25,
          left: 0,
          bottom: 20
        }}
      >
        <CartesianGrid stroke='#D5D5D5' strokeDasharray="3 3"/>
        <XAxis 
          axisLine={{ stroke: '#D5D5D5' }} 
          tickLine={{ stroke: '#FFFFFF' }} 
          dy={15} 
          tick={<CustomTick/>} 
          tickCount={100} 
          dataKey="key"
        />
        <YAxis 
          axisLine={{ stroke: '#D5D5D5' }} 
          tickLine={{ stroke: '#FFFFFF' }} 
          dx={-15} 
          tick={{ fontSize: 12 }}
          domain={(chartData || []).every(dataPoint => dataPoint.value < 4) ? [0, 4] : undefined}
        />
        <Tooltip/>
        <Line 
          type="monotone" 
          dataKey="value" 
          stroke="#FFC600" 
          strokeWidth={2} 
          dot={false} 
          activeDot={true} 
        />
      </LineChart>
    </ResponsiveContainer>

  )
}

const COLORS = ["#FFC600", "#656565", "#858585", "#A5A5A5", "#C5C5C5", "#E5E5E5", "#F5F5F5"];

const renderActiveShape = (props) => {
  const RADIAN = Math.PI / 180;
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
    value,
    name
  } = props;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 30) * cos;
  const my = cy + (outerRadius + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? "start" : "end";

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />

      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        fill="none"
      />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        className={styles.pieChartValue}
        textAnchor={textAnchor}
        fill="#333"
      >{`${(percent * 100).toFixed(0)}%`}
      </text>
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        className={styles.pieChartNames}
        dy={18}
        textAnchor={textAnchor}
        fill="#999"
      >
        {name}
      </text>
    </g>
  );
};

export function TrendsPieChart({ data, viewOption, height, width }) {

  let pieChartData = data.map((item) => {
    return {
      name: _.get(item, viewOption.name),
      value: item[viewOption.value]
    }
  });

  return (
    <ResponsiveContainer width={width} height={height}>
      <PieChart>
        <Pie
          data={pieChartData}
          cx="50%"
          cy="50%"
          blendStroke={true}
          innerRadius={height > 320 ? 70 : 40}
          outerRadius={height > 320 ? 130 : 100}
          label={renderActiveShape}
          fill="#8884d8"
          paddingAngle={0}
          dataKey="value"
        >
          {pieChartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
}

export function TrendBarChart({ width, data, dataKey }) {

  const [focusBar, setFocusBar] = useState(null);
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataItems = payload[0].payload;
      const thumbnail = _.get(dataItems, "thumbnail", null)
      const fallback = dataKey === "views" ? 0 : formatDurationWithLabel(_.get(dataItems, "totalTime", '')).value
      return (
        <div>
          <div className={cx(styles.toolTipContainer, { [styles.changePosition] :thumbnail})}>
            <p className={styles.line1}>
              <Truncate width={75} lines={1}>{_.get(dataItems, "name") || `#${_.get(dataItems, "index", '')}`}</Truncate>
            </p>
            {thumbnail && <img className={styles.thumbnail} src={thumbnail} height={80} width={80}/>}
            <p className={styles.line2}>{_.get(dataItems, "views") || fallback}</p>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <ResponsiveContainer width={width} height={50}>
      <BarChart width={30} height={40} data={data}
        onMouseMove={state => {
          if (state.isTooltipActive) {
            setFocusBar(state.activeTooltipIndex);
          } else {
            setFocusBar(null);
          }
        }}>
        <Tooltip cursor={{ fill: '#F5F5F5' }} content={<CustomTooltip />} />
        <Bar minPointSize={3} dataKey={dataKey}>
          {(data || []).map((entry, index) => (
            <Cell key={`cell-${index}`} fill={focusBar === index ? '#ffc600' : '#d5d5d5'} />
          ))}
        </Bar>

      </BarChart>
    </ResponsiveContainer>
  );
}

export function TrendsAreaChart({ data, height, width }) {

  if (!data || data.length < 1) {
    return null;
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataItems = payload[0].payload;
      return (
        <div>
          <div className={styles.toolTipContainer}>
            <p className={styles.line1}>Views: <Truncate width={75} lines={1}>{_.get(dataItems, "count", '')}</Truncate></p>
            <p className={styles.line2}>{formatDurationWithLabel(_.get(dataItems, 'currentTime', 0)).value}</p>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <ResponsiveContainer width={width} height={height}>
      <AreaChart
        data={data}
        margin={{
          top: 5,
          right: 5,
          left: 20,
          bottom: 5
        }}
      >
        <XAxis axisLine={{ stroke: '#D5D5D5' }} tickLine={{ stroke: '#FFFFFF' }} dy={15} hide={true} dataKey="index" />
        <YAxis axisLine={{ stroke: '#D5D5D5' }} tickLine={{ stroke: '#FFFFFF' }} dx={-15} hide={true} dataKey="count"/>
        <Tooltip cursor={{ fill: '#F5F5F5' }} content={<CustomTooltip />} />
        <Area type="monotone" dataKey="count" stroke="#FFC600" fill="#FFC600" fillOpacity={0.1} />
      </AreaChart>
    </ResponsiveContainer>
  )
}
